import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Plus } from "react-bootstrap-icons";
import { confirmAlert } from "react-confirm-alert";
import { toast } from "react-toastify";
import { BaseButton, ConfirmModal, IMemberPlan } from "../../libs";
import { paymentService } from "../../services";
import SearchIcon from "../../assets/images/icons/search.svg";
import { Loader } from "../ui";
import { CouponCard } from "./coupon-card";
import { CouponModal } from "./coupon-modal";
import { RedemptionLimitModal } from "./redemption-limit-modal/RedemptionLimitModal";
import { CouponCode } from "./types";
import * as Styled from "./ManageCouponCodesComponent.styled";

const errorMessage = (error: any, fallback: string) =>
  error?.response?.data?.error || error?.response?.data?.message || fallback;

export const ManageCouponCodesComponent: React.FC = () => {
  const [coupons, setCoupons] = useState<CouponCode[]>([]);
  const [plans, setPlans] = useState<IMemberPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingRedemptions, setEditingRedemptions] = useState<CouponCode | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [couponData, planData] = await Promise.all([
        paymentService.getCoupons(),
        paymentService.getCustomerProductPlans(),
      ]);
      setCoupons(couponData);
      setPlans(planData);
    } catch (error) {
      console.error("Error loading coupon codes:", error);
      toast.error(errorMessage(error, "Failed to load coupon codes"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);
  const filteredCoupons = useMemo(
    () =>
      coupons.filter((coupon) =>
        `${coupon.name} ${coupon.code}`.toLowerCase().includes(searchQuery.toLowerCase())
      ),
    [coupons, searchQuery]
  );
  const redemptions = coupons.reduce((total, coupon) => total + (coupon.redemptionCount || 0), 0);

  const createCoupon = async (values: Record<string, unknown>) => {
    try {
      await paymentService.createCoupon(values);
      toast.success("Coupon code created");
      await fetchData();
    } catch (error) {
      toast.error(errorMessage(error, "Failed to create coupon code"));
      throw error;
    }
  };

  const toggleStatus = (coupon: CouponCode, enable: boolean) => {
    if (coupon.active === enable) return;
    confirmAlert({
      customUI: ({ onClose }) => (
        <ConfirmModal
          title={enable ? "Activate Coupon" : "Deactivate Coupon"}
          description={[
            { text: `${enable ? "Activate" : "Deactivate"} “${coupon.code}”?` },
            {
              text: enable
                ? "Customers can use this code again within its configured limits."
                : "Customers will no longer be able to use this code.",
            },
          ]}
          noText="Cancel"
          yesText={enable ? "Activate" : "Deactivate"}
          danger={!enable}
          onNo={onClose}
          onYes={async () => {
            try {
              const updated = await paymentService.updateCoupon(coupon.id, { active: enable });
              setCoupons((current) =>
                current.map((item) => (item.id === coupon.id ? { ...item, ...updated } : item))
              );
              toast.success(`Coupon ${enable ? "activated" : "deactivated"}`);
            } catch (error) {
              toast.error(
                errorMessage(error, `Failed to ${enable ? "activate" : "deactivate"} coupon`)
              );
            }
            onClose();
          }}
        />
      ),
      closeOnEscape: true,
      closeOnClickOutside: false,
    });
  };

  const saveRedemptionLimit = async (maxRedemptions: number | null) => {
    if (!editingRedemptions) return;
    try {
      const updated = await paymentService.updateCoupon(editingRedemptions.id, { maxRedemptions });
      setCoupons((current) =>
        current.map((coupon) => (coupon.id === updated.id ? { ...coupon, ...updated } : coupon))
      );
      setEditingRedemptions((current) => (current ? { ...current, ...updated } : current));
      toast.success(
        maxRedemptions == null
          ? "Coupon redemptions set to unlimited"
          : `Redemption limit updated to ${maxRedemptions}`
      );
    } catch (error) {
      toast.error(errorMessage(error, "Failed to update redemption limit"));
      throw error;
    }
  };

  if (loading) return <Loader />;
  return (
    <Styled.StyledContainer>
      <Styled.Header>
        <Styled.HeaderLeftSection>Coupon Codes</Styled.HeaderLeftSection>
        <Styled.HeaderRightSection>
          <Styled.SearchBarWrapper>
            <Styled.SearchIcon src={SearchIcon} alt="Search" />
            <Styled.SearchInput
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search coupon codes"
            />
          </Styled.SearchBarWrapper>
          <BaseButton onClick={() => setShowModal(true)}>
            <Styled.NewButtonContent>
              <Plus size={26} /> New Coupon
            </Styled.NewButtonContent>
          </BaseButton>
        </Styled.HeaderRightSection>
      </Styled.Header>
      <Styled.Description>
        Create Stripe-backed coupon codes for membership plans. Referral and public discounts never
        stack; checkout applies the single best eligible discount.
      </Styled.Description>
      <Styled.StatsGrid>
        <Styled.StatCard>
          <Styled.StatValue>{coupons.filter((coupon) => coupon.active).length}</Styled.StatValue>
          <Styled.StatLabel>Active coupons</Styled.StatLabel>
        </Styled.StatCard>
        <Styled.StatCard>
          <Styled.StatValue>{coupons.length}</Styled.StatValue>
          <Styled.StatLabel>Total coupons</Styled.StatLabel>
        </Styled.StatCard>
        <Styled.StatCard>
          <Styled.StatValue>{redemptions}</Styled.StatValue>
          <Styled.StatLabel>Total redemptions</Styled.StatLabel>
        </Styled.StatCard>
      </Styled.StatsGrid>
      <Styled.List>
        {filteredCoupons.length ? (
          filteredCoupons.map((coupon) => (
            <CouponCard
              key={coupon.id}
              coupon={coupon}
              onToggleStatus={(enabled) => toggleStatus(coupon, enabled)}
              onEditRedemptions={() => setEditingRedemptions(coupon)}
            />
          ))
        ) : (
          <Styled.NoDataWrapper>
            No coupon codes found. Create a coupon to get started.
          </Styled.NoDataWrapper>
        )}
      </Styled.List>
      <CouponModal
        show={showModal}
        onHide={() => setShowModal(false)}
        plans={plans}
        onSave={createCoupon}
      />
      <RedemptionLimitModal
        show={!!editingRedemptions}
        onHide={() => setEditingRedemptions(null)}
        coupon={editingRedemptions}
        onSave={saveRedemptionLimit}
      />
    </Styled.StyledContainer>
  );
};
